import { relewiseFallback, relewiseTags } from '@/shared/constants/constants';
import { settleWithin } from '@/shared/utils/promise';
import {
  isRelewiseFailureReason,
  type RelewiseFailureReason
} from '@/shared/utils/relewise-fallback-reason';
import { hobbiiSessionStorage } from '@/shared/utils/web-storage';

export type RelewiseFailure = {
  reason: RelewiseFailureReason;
  endpoint: string;
  durationMs: number;
  cooldown: boolean;
};

type StoredFallback = {
  until: number;
  reason: RelewiseFailureReason;
};

type FetchArgs = Parameters<typeof fetch>;

const listeners = new Set<(failure: RelewiseFailure) => void>();
const pendingChecks = new Map<string, Set<Promise<void>>>();
let originalFetch: typeof fetch | null = null;

export const isFallbackEnabled = (): boolean => {
  const flags = window.Hobbii?.flags;
  return Boolean(flags?.enableRelewiseSearch && flags?.enableRelewiseFallback);
};

export const startFallbackCooldown = (reason: RelewiseFailureReason): void => {
  try {
    const value: StoredFallback = {
      until: Date.now() + relewiseFallback.COOLDOWN_MS,
      reason
    };
    hobbiiSessionStorage.setItem(
      relewiseFallback.STORAGE_KEY,
      JSON.stringify(value)
    );
  } catch {
    // Storage unavailable: Relewise is simply retried on every page.
  }
};

export const getActiveFallback = (): {
  reason: RelewiseFailureReason;
} | null => {
  try {
    const raw = hobbiiSessionStorage.getItem(relewiseFallback.STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: Partial<StoredFallback> | null = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.until !== 'number' ||
      !isRelewiseFailureReason(parsed.reason)
    ) {
      return null;
    }

    if (Date.now() >= parsed.until) {
      hobbiiSessionStorage.removeItem(relewiseFallback.STORAGE_KEY);
      return null;
    }

    return { reason: parsed.reason };
  } catch {
    return null;
  }
};

export const isRelewiseAppLoaded = (): boolean =>
  Boolean(customElements.get(relewiseTags.SEARCH_TAG));

const getOriginalFetch = (): typeof fetch => originalFetch ?? window.fetch;

export const isOwnDomainReachable = async (): Promise<boolean> => {
  if (navigator.onLine === false) {
    return false;
  }

  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    relewiseFallback.PROBE_TIMEOUT_MS
  );

  try {
    const response = await Reflect.apply(getOriginalFetch(), window, [
      relewiseFallback.PROBE_PATH,
      { method: 'HEAD', cache: 'no-store', signal: controller.signal }
    ]);
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

const getRequestUrl = (input: FetchArgs[0]): string => {
  if (typeof input === 'string') {
    return input;
  }
  return input instanceof URL ? input.href : input.url;
};

const isRelewiseApiUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url, window.location.href);
    return (
      hostname === relewiseFallback.API_HOST ||
      hostname.endsWith(relewiseFallback.API_HOST_SUFFIX) ||
      hostname.endsWith(relewiseFallback.API_SUBDOMAIN_SUFFIX)
    );
  } catch {
    return false;
  }
};

const getEndpoint = (url: string): string => {
  try {
    const segments = new URL(url, window.location.href).pathname
      .split('/')
      .filter(Boolean);
    return segments[segments.length - 1] ?? '';
  } catch {
    return '';
  }
};

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === relewiseFallback.ABORT_ERROR_NAME;

const report = (failure: RelewiseFailure) => {
  if (failure.cooldown) {
    startFallbackCooldown(failure.reason);
  }

  listeners.forEach((listener) => {
    try {
      listener(failure);
    } catch (e) {
      console.error('Error handling Relewise fetch failure:', e);
    }
  });
};

const trackPendingCheck = (endpoint: string, done: Promise<void>) => {
  const pending = pendingChecks.get(endpoint) ?? new Set<Promise<void>>();
  pending.add(done);
  pendingChecks.set(endpoint, pending);

  void done.then(() => {
    pending.delete(done);
    if (pending.size === 0 && pendingChecks.get(endpoint) === pending) {
      pendingChecks.delete(endpoint);
    }
  });
};

export const whenRelewiseChecksSettled = async (
  endpoint: string
): Promise<void> => {
  const pending = pendingChecks.get(endpoint);
  if (pending) {
    await Promise.all([...pending]);
  }
};

const canRetry = ([input, init]: FetchArgs): boolean =>
  !(input instanceof Request) &&
  (init?.body === undefined || typeof init.body === 'string');

const observeRelewiseRequest = (
  promise: Promise<Response>,
  args: FetchArgs,
  url: string
) => {
  const [input, init] = args;
  const signal =
    init?.signal ?? (input instanceof Request ? input.signal : null);
  const endpoint = getEndpoint(url);
  const startedAt = Date.now();
  let settled = false;
  let durationMs = 0;
  let wasHidden = document.visibilityState === 'hidden';
  let markDone: () => void = () => undefined;
  trackPendingCheck(
    endpoint,
    new Promise<void>((resolve) => {
      markDone = resolve;
    })
  );

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      wasHidden = true;
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  const finish = () => {
    settled = true;
    durationMs = Date.now() - startedAt;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  const fail = (reason: RelewiseFailureReason, cooldown: boolean) =>
    report({
      reason,
      endpoint,
      durationMs,
      cooldown
    });

  const onTimeout = async () => {
    if (settled) {
      return;
    }
    finish();
    if (wasHidden || signal?.aborted) {
      return;
    }
    if ((await isOwnDomainReachable()) && !signal?.aborted) {
      fail('timeout', false);
    }
  };

  const timer = setTimeout(() => {
    onTimeout()
      .catch(() => undefined)
      .finally(markDone);
  }, relewiseFallback.TIMEOUT_MS);

  const onResponse = (response: Response) => {
    if (settled) {
      return;
    }
    finish();
    try {
      if (response.status >= relewiseFallback.SERVER_ERROR_MIN_STATUS) {
        fail('server_error', true);
      }
    } finally {
      markDone();
    }
  };

  const handleError = async (error: unknown) => {
    if (isAbortError(error) || signal?.aborted) {
      return;
    }

    if (canRetry(args)) {
      try {
        const retry = await settleWithin<Response>(
          Reflect.apply(getOriginalFetch(), window, args),
          relewiseFallback.TIMEOUT_MS
        );
        if (retry && retry.status < relewiseFallback.SERVER_ERROR_MIN_STATUS) {
          return;
        }
      } catch (retryError) {
        if (isAbortError(retryError) || signal?.aborted) {
          return;
        }
      }
    }

    if (signal?.aborted) {
      return;
    }

    if ((await isOwnDomainReachable()) && !signal?.aborted) {
      fail('network_error', true);
    }
  };

  const onError = (error: unknown) => {
    if (settled) {
      return;
    }
    finish();
    return handleError(error)
      .catch(() => undefined)
      .finally(markDone);
  };

  promise.then(onResponse, onError).catch(() => undefined);
};

export const installRelewiseFetchMonitor = (
  onFailure: (failure: RelewiseFailure) => void
): void => {
  listeners.add(onFailure);

  if (originalFetch) {
    return;
  }

  const original = window.fetch;
  originalFetch = original;

  window.fetch = (...args: FetchArgs): Promise<Response> => {
    const promise: Promise<Response> = Reflect.apply(original, window, args);
    const url = getRequestUrl(args[0]);

    if (isRelewiseApiUrl(url)) {
      observeRelewiseRequest(promise, args, url);
    }

    return promise;
  };
};

/** Test-only: restore fetch and drop listeners so each spec starts clean. */
export const __resetRelewiseFetchMonitorForTests = () => {
  if (originalFetch) {
    window.fetch = originalFetch;
  }
  originalFetch = null;
  listeners.clear();
  pendingChecks.clear();
};
